import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function RequestDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title:"", description:"", priority:"Normal", deadline:"" });
  const [busy, setBusy] = useState(false);
  useEffect(()=>{ if (open) setForm({ title:"", description:"", priority:"Normal", deadline:"" }); }, [open]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.post("/requests", form);
      toast.success("Request sent to your team");
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-lg" data-testid="request-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-orange-400"/>Request New Creative</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="What do you need?" required><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="req-title" placeholder="e.g. Diwali carousel for Instagram" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <F label="Brief"><Textarea rows={5} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} data-testid="req-description" placeholder="Target audience, mood, key message, dos and don'ts..." className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Priority">
              <Select value={form.priority} onValueChange={v=>setForm({...form, priority:v})}>
                <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="req-priority"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                  {["Low","Normal","High"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Deadline"><Input type="date" value={form.deadline} onChange={e=>setForm({...form, deadline:e.target.value})} data-testid="req-deadline" className="bg-[#FFFFFF] border-[#E4E7EC]"/></F>
          </div>
          <div className="text-[11px] font-mono text-neutral-500 pt-2 border-t border-[#E4E7EC]">Your assigned team will be notified immediately.</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="req-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.title.trim()} data-testid="req-save" className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Sending..." : "Send Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, children}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">{label}{required && <span className="text-rose-400 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}
